import { useAppStore } from "@/store/utils/hooks";
import { observer } from "mobx-react-lite";
import { SessionEventObservable } from "@/store/sessions/SessionEventObservable";
import {
  MemCommonMarkdown,
  OnClickMemItem,
  CommonMentionKindForRoutableKind,
} from "@mem-labs/common-editor";
import { useCallback } from "react";
import {
  StyledReasoningMessage,
  StyledActionMessage,
  StyledCommunicationMessage,
} from "@/components/active-session/session/session-event.styles";
import { MDSIcon, MDSIconColor, MDSIconKind } from "@/mds";

interface SessionEventProps {
  event: SessionEventObservable;
}

export const SessionEvent: React.FC<SessionEventProps> = observer(function SessionEvent({ event }) {
  const { store } = useAppStore();

  const handleClickMemItem: OnClickMemItem = useCallback(
    (itemKind, itemId, target) => {
      const mentionKind = CommonMentionKindForRoutableKind(itemKind);
      if (mentionKind) {
        store.navigation.goToMention({
          id: itemId,
          mentionKind,
          url: "",
          highlightText: target.textContent ?? undefined,
        });
      } else {
        if (itemKind === "imports") {
          const [importBatchId, listKind] = itemId.split("/");
          switch (listKind) {
            case "notes":
              store.navigation.goToImportBatchNotes({
                importBatchId: importBatchId,
              });
              break;
            case "collections":
              store.navigation.goToImportBatchCollections({
                importBatchId: importBatchId,
              });
              break;
          }
        }
      }
    },
    [store.navigation]
  );

  if (!event.eventValue.content && !event.modelData.event_value_json.content) return null;

  let content = event.eventValue.content as string;
  if (event.modelData.kind === "MESSAGE_SENT") {
    // Override the content if the event is a message sent event, since the display text
    // is stored in the event_value_json field.
    // TODO: Change event_value_json to more a generic related_model_data field.
    content = event.modelData.event_value_json.content as string;
  }

  switch (event.modelData.event_value_json.kind) {
    case "REASONING":
      return <StyledReasoningMessage>{content}</StyledReasoningMessage>;
    case "ACTION":
      return (
        <StyledActionMessage>
          <MemCommonMarkdown unstyled onClickMemItem={handleClickMemItem}>
            {content}
          </MemCommonMarkdown>
          <MDSIcon kind={MDSIconKind.Check} size={15} color={MDSIconColor.Positive} />
        </StyledActionMessage>
      );
    case "COMMUNICATION":
      return <StyledCommunicationMessage>{content}</StyledCommunicationMessage>;
    default:
      return null;
  }
});

SessionEvent.displayName = "SessionEvent";
